import {
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useToast } from 'hooks/use-toast';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';
import {
  changePassword,
  createAccount,
  deactivateAccount,
  getAccount,
  getUserById,
  updateAccount,
  updateUserAccess,
} from '../services/accounts-service';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import {
  CreateUserFormType,
  ProfileFormType,
} from 'features/profile/utils/utils';
import { User } from '/types/user-type';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useNavigate } from 'react-router-dom';
import { completeLogout } from 'features/auth/utils/complete-logout';

/**
 * Custom hook to fetch account details.
 * It uses a global query to fetch data related to the account.
 *
 * @returns {Object} The query result for getting the account.
 *
 * @example
 * const { data, error, isLoading } = useGetAccount();
 */
export const useGetAccount = (option: any): UseQueryResult<User> => {
  const { configurationSettings } = useConfig();
  return useGlobalQuery({
    queryKey: ['getAccount'],
    queryFn: () => getAccount(configurationSettings),
    ...option,
    gcTime: 0,
  });
};

/**
 * Custom hook to fetch user details by user ID.
 * It uses a global query to fetch data for a specific user.
 *
 * @param {string} userId - The ID of the user to fetch
 * @param {Object} [options] - Optional query configuration
 * @returns {Object} The query result for getting the user by ID.
 *
 * @example
 * const { data, error, isLoading } = useGetUserById(userId, { enabled: true });
 */
export const useGetUserById = (userId: string, options?: any) => {
  const { configurationSettings } = useConfig();
  return useGlobalQuery({
    queryKey: ['getUserById', userId],
    queryFn: () => getUserById(userId, configurationSettings),
    enabled: !!userId,
    gcTime: 0,
    staleTime: 0,
    ...options,
  });
};

/**
 * Custom hook to create a new account.
 * It uses a global mutation to create a new account and handle success or error states.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the account creation is successful.
 *
 * @returns {Object} The mutation result for creating the account.
 *
 * @example
 * const { mutate } = useCreateAccount({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */

export const useCreateAccount = (options?: { onSuccess?: () => void }) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();

  return useGlobalMutation({
    mutationKey: ['createAccount'],
    mutationFn: (data: CreateUserFormType) => createAccount(data, configurationSettings),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('USER_ADD_SUCCESS_DESCRIPTION'),
      });

      options?.onSuccess?.();
    },
    onError: (error: any) => {
      // Check if the error contains specific field errors like "Email already in use"
      let errorMessage = t('USER_ADD_FAILED_DESCRIPTION');

      if (error.error.errors) {
        // Handle field-specific errors from the API response
        const fieldErrors = Object.values(error.error.errors);
        if (fieldErrors.length > 0) {
          errorMessage = fieldErrors.join(', ');
        }
      } else if (error?.error?.message) {
        errorMessage = error.error.message;
      }

      if (errorMessage == 'Email already in use') {
        errorMessage = t('EMAIL_ALREADY_IN_USE');
      }

      if (errorMessage == 'User limit reached') {
        errorMessage = t('USER_LIMIT_REACHED');
      }

      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description: errorMessage,
      });
    },
  });
};

/**
 * Custom hook to update account details.
 * It uses a global mutation to update the account information and handle success or error states.
 * It also invalidates the account query cache upon success.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the account update is successful.
 *
 * @returns {Object} The mutation result for updating the account.
 *
 * @example
 * const { mutate } = useUpdateAccount({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */
export const useUpdateAccount = (options?: { onSuccess?: () => void }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const currentUserId = useCommonStore((state) => state.user?.id);

  return useGlobalMutation({
    mutationKey: ['updateAccount'],
    mutationFn: async (
      data: ProfileFormType & {
        userId: string;
        syncAccess?: boolean;
        accessOrganizationId?: string;
      }
    ) => {
      const { syncAccess, accessOrganizationId, userId, ...profileData } = data;
      const isSelf = !!currentUserId && userId === currentUserId;
      const result = await updateAccount(profileData, configurationSettings, userId, isSelf);

      if (syncAccess) {
        await updateUserAccess(
          {
            roles: profileData.roles ?? [],
            permissions: profileData.permissions ?? [],
            organizationId: accessOrganizationId || 'default',
            userId,
          },
          configurationSettings
        );
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAccount'] });
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('PROFILE_UPDATE_SUCCESS'),
      });

      options?.onSuccess?.();
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description:
          error?.error?.message ?? `${t('PROFILE_UPDATE_FAILED')} ${t('PLEASE_CHECK_INPUT')}`,
      });
    },
  });
};

/**
 * Custom hook to change the user's password.
 * It uses a global mutation to update the password and display appropriate success or error messages.
 *
 * @returns {Object} The mutation result for changing the password.
 *
 * @example
 * const { mutate } = useChangePassword();
 */
export const useChangePassword = () => {
  const { toast } = useToast();
  const { configurationSettings } = useConfig();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationKey: ['changePassword'],
    mutationFn: (data: { oldPassword: string; newPassword: string }) =>
      changePassword({
        ...data,
        config: configurationSettings,
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('PASSWORD_UPDATE_SUCCESS'),
      });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description: t('CHECK_PASSWORD'),
      });
    },
  });
};

/**
 * Custom hook to deactivate the user's account.
 * It uses a global mutation to deactivate the account and handle success or error states.
 * Upon success, it clears the user data and navigates to the sign-in page.
 *
 * @returns {Object} The mutation result for deactivating the account.
 *
 * @example
 * const { mutate } = useDeactivateAccount();
 */
export const useDeactivateAccount = ({isDeleteForever = false}: {isDeleteForever?: boolean}) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const user = useCommonStore((state) => state.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useGlobalMutation({
    mutationKey: ['deactivateAccount'],
    mutationFn: () => {
      if (!user?.id) {
        throw new Error('User ID not found');
      }
      return deactivateAccount(configurationSettings, user.id, isDeleteForever);
    },
    onSuccess: (response) => {
      if (response.isSuccess) {
        completeLogout(queryClient, navigate);

        toast({
          variant: 'success',
          title: t('SUCCESS'),
          description: isDeleteForever ? t('ACCOUNT_DELETED_SUCCESSFULLY') : t('ACCOUNT_DEACTIVATED_SUCCESSFULLY'),
        });
      } else {
        throw new Error(response.errors || 'Failed to deactivate account');
      }
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description: error?.error?.message ?? (isDeleteForever ? t('ACCOUNT_DELETION_FAILED') : t('ACCOUNT_DEACTIVATION_FAILED')),
      });
    },
  });
};

export const ACCOUNT_QUERY_KEY = ['account'];
